import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@/common/infrastructure/http/apiClient';
import { toNumberOrNull } from '@/blocks/application/helpers/toNumberOrNull';
import type { PdfBlockUsageDto } from '@/generated/models/pdfBlockUsageDto';

/**
 * One row per (template, template version, block version): ptb groups the placements of a block
 * that way, so a template can appear several times (and several template versions can share one
 * block version). Count and list by `templateId`, never by row.
 */
export interface BlockUsage {
  templateId: number;
  templateName: string;
  templateVersionId: number | null;
  versionNumber: number | null;
  /** The block version placed in the template (a string, like `BlockVersion.blockVersionId`). Null when the API predates PDF-3. */
  blockVersionId: string | null;
  blockVersionNumber: number | null;
  instanceCount: number;
  isPublished: boolean;
}

const toBlockUsage = (dto: PdfBlockUsageDto): BlockUsage => ({
  templateId: Number(dto.templateId),
  templateName: dto.templateName ?? '',
  templateVersionId: toNumberOrNull(dto.templateVersionId),
  versionNumber: toNumberOrNull(dto.versionNumber),
  blockVersionId: dto.blockVersionId != null ? String(dto.blockVersionId) : null,
  blockVersionNumber: toNumberOrNull(dto.blockVersionNumber),
  instanceCount: Number(dto.instanceCount ?? 0),
  isPublished: dto.isPublished ?? false
});

export const blockUsagesQueryKey = (blockId: string) => ['document-builder', 'block', blockId, 'usages'] as const;

export const useBlockUsages = (blockId: string | null) => {
  return useQuery({
    queryKey: blockId ? blockUsagesQueryKey(blockId) : ['document-builder', 'block', null, 'usages'],
    enabled: !!blockId,
    queryFn: async (): Promise<BlockUsage[]> => {
      if (!blockId) return [];

      const rows = await customInstance<PdfBlockUsageDto[]>({
        url: `/api/pdf-templates/v1/blocks/${blockId}/usages`,
        method: 'GET'
      });

      return (rows ?? []).map(toBlockUsage);
    },
    staleTime: 1000 * 60
  });
};
